import { ErrorBoundary, SuspenseLoader, useAppUser, useConfig } from '@bechtel/client-common';
import { Box, Icon, Text, useSideSheetState } from '@bechtel/gds';
import { BlockStatusCode, CommodityStatusCode, ItrMilestoneAction, ItrType, MilestoneStatusCode } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { every, map, some } from 'lodash';
import { FC, Suspense } from 'react';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { MilestoneAction } from './action';
import { MilestoneActionButtonStyled } from './action/styled';
import { MilestoneAttachmentAction } from './attachment';
import { MilestoneHours } from './hours';
import { MilestoneLinearFeet } from './linear-feet';
import { AssociatedWeldsMetadata, MilestoneMetadata } from './metadata';
import {
  CommodityMilestoneListStyled,
  MilestoneAttachmentContainerStyled,
  MilestoneBodyStyled,
  MilestoneCardStyled,
  MilestoneIndicatorStyled,
  MilestoneTitleStyled,
} from './styled';
import { MilestoneListProps, MilestoneProps } from './types';
import WeldDetails from './weld-details/internals';
import WeldFlangeConnections from './weld-flange-connections';

export const Milestone: FC<MilestoneProps> = ({ hidden, milestone, isUserClaimer, isUnitOfMeasureHour }) => {
  const { features } = useConfig();
  const hasOpenBlocks = some(milestone.blocks, { currentStatusI18n: BlockStatusCode.BLOCK_OPENED });
  const status = hasOpenBlocks ? MilestoneStatusCode.BLOCKED : milestone.currentStatus.codeI18n;
  const { assignedCrewMembers } = useWorkPackage();
  const { canApproveMilestone, canRejectMilestone } = useWorkPackagePermissions(assignedCrewMembers);
  const hasAttachments = !!milestone.attachments.length;
  const { visible: weldDetailVisible, open: weldDetailOpen, close: weldDetailClose } = useSideSheetState(false);
  const weldIds = map(milestone.weldStatuses, 'id');
  const hasAssociatedWelds = milestone.weldStatuses && milestone.weldStatuses.length > 0;
  const isFullCardLinkRequired = !!(hasAssociatedWelds && canApproveMilestone && canRejectMilestone);
  const isFitupEnabled = features.canApproveFitupInspection ? features.canApproveFitupInspection : false;
  const itrRequired = some(milestone.type.projectItrConfig, (item) => {
    return item.itrType === ItrType.PIPE_CLEANLINESS && item?.itrMilestoneAction === ItrMilestoneAction.CLAIM;
  });

  const showViewDetailLabel =
    isFitupEnabled &&
    hasAssociatedWelds &&
    every(milestone.weldStatuses, (weld) => weld?.status?.status == CommodityStatusCode.VT_APPROVED);
  return (
    <MilestoneCardStyled status={status}>
      <MilestoneIndicatorStyled status={status} />
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} hidden={hidden}>
        <MilestoneTitleStyled status={status}>
          {milestone.name}
          {hasAttachments && canApproveMilestone && canRejectMilestone && (
            <MilestoneAttachmentContainerStyled>
              <MilestoneAttachmentAction milestone={milestone} status={status} isInverted={true} />
            </MilestoneAttachmentContainerStyled>
          )}
        </MilestoneTitleStyled>
        <MilestoneBodyStyled
          isFullCardLinkRequired={isFullCardLinkRequired}
          onClick={() => {
            isFullCardLinkRequired && weldDetailOpen();
          }}
        >
          {isUnitOfMeasureHour && <MilestoneHours status={status} hours={milestone.hours} />}
          {!isUnitOfMeasureHour && <MilestoneLinearFeet status={status} quantity={milestone.quantity} />}
          <MilestoneMetadata status={status} milestone={milestone} />
          {hasAssociatedWelds && (
            <Box pt={{ sm: '300' }} flexDirection={{ sm: 'column' }}>
              <AssociatedWeldsMetadata weldStatuses={milestone.weldStatuses}></AssociatedWeldsMetadata>
            </Box>
          )}
        </MilestoneBodyStyled>
        <Box
          pb={{ sm: '400' }}
          mt={{ sm: '100' }}
          px={{ sm: '400' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'flex-end' }} //Justify-content to flex end  to show weld details/attachment button at the leftmost end if milestone action section is not visible
        >
          <MilestoneAction status={status} milestone={milestone} isUserClaimer={isUserClaimer} />
          {hasAssociatedWelds &&
            (status === MilestoneStatusCode.CLAIMED ? !canApproveMilestone && !canRejectMilestone : true) && (
              <Box onClick={weldDetailOpen} flexGrow={{ sm: 1 }} ml={{ sm: `${showViewDetailLabel ? '300' : '000'}` }}>
                <MilestoneActionButtonStyled status={status} data-testid="weld-details-btn">
                  <Icon icon="list"></Icon>
                  {!showViewDetailLabel && (
                    <Box ml={{ sm: '400' }}>
                      <Text color="grey.1000" size="300">
                        <Trans>View Details</Trans>
                      </Text>
                    </Box>
                  )}
                </MilestoneActionButtonStyled>
              </Box>
            )}
          {weldDetailVisible && weldIds.length && (
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              {isFitupEnabled ? (
                <WeldFlangeConnections
                  close={weldDetailClose}
                  weldIds={weldIds}
                  itrRequired={itrRequired}
                  isUnitOfMeasureHour={isUnitOfMeasureHour}
                />
              ) : (
                <WeldDetails close={weldDetailClose} weldList={milestone.weldStatuses} />
              )}
            </Suspense>
          )}
          {hasAttachments && !canApproveMilestone && !canRejectMilestone && (
            <MilestoneAttachmentAction milestone={milestone} status={status} />
          )}
        </Box>
      </Box>
    </MilestoneCardStyled>
  );
};

export const MilestoneList: FC<MilestoneListProps> = ({ hidden, isUnitOfMeasureHour, milestones, activityHistory }) => {
  const { user } = useAppUser();
  return (
    <CommodityMilestoneListStyled milestonesCount={milestones.length}>
      {milestones.map((milestone) => {
        const isUserClaimer =
          milestone?.currentStatus?.codeI18n === MilestoneStatusCode.CLAIMED &&
          milestone?.currentStatus?.createdBy?.id == user?.id;
        return (
          <ErrorBoundary key={milestone.name}>
            <Milestone
              milestone={milestone}
              hidden={hidden}
              isUserClaimer={isUserClaimer}
              isUnitOfMeasureHour={isUnitOfMeasureHour}
            />
          </ErrorBoundary>
        );
      })}
    </CommodityMilestoneListStyled>
  );
};
